import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { RoleModel } from 'src/app/_Models/user/role.model';
import { UpdateSessionModel, UserModel } from 'src/app/_Models/user/user.model';
import { UserLoginModel } from 'src/app/_Models/useraccount/userLogin.model';
import { OAuthConfig } from 'src/assets/authconfig/AuthConfig';
import { UtilityService } from '../common/utility.service';
import { UserProfileService } from '../UserAccount/userprofile.service';
import { Permission } from 'src/app/components/common/static-data-common';

@Injectable({ providedIn: 'root' })

export class OAuthImplicitFlowService {

  identityBaseurl: string;
  public user: UserModel;
  data: any;
  apiUrl: any;
  private subDomainName: string;

  constructor(@Inject('IDENTITYSERVER_URL') identityBaseUrl: string,
    @Inject('BASE_URL') _apiURL: string,
    private oAuthService: OAuthService,
    private http: HttpClient,
    private userService: UserProfileService,
    private router: Router,
    private utility: UtilityService) {

    this.identityBaseurl = this.utility.removeSlashFromUrlAtEnd(identityBaseUrl);
    this.apiUrl = this.utility.removeSlashFromUrlAtEnd(_apiURL);
  }

  configureService() {
    // Check if configuration is already done by checking one of the required properties
    if (this.oAuthService.issuer) {
      return;
    }

    // Get origin and baseref to generate baseUrl of angular app
    let origin = new URL(window.location.href).origin;
    let baseUrl = document.getElementsByTagName('base')[0].href;

    origin = this.utility.removeSlashFromUrlAtEnd(origin);

    if (!baseUrl) {
      baseUrl = origin;
    } else {
      baseUrl = this.utility.removeSlashFromUrlAtEnd(baseUrl);
    }

    if (!baseUrl.toLowerCase().includes(origin.toLowerCase())) {
      baseUrl = origin + baseUrl
    }

    this.subDomainName = this.utility.getSubdomainFromUrl();

    let objAuthConfig = OAuthConfig;
    objAuthConfig.issuer = this.identityBaseurl + objAuthConfig.issuer.toString();

    if (this.subDomainName && this.subDomainName != ''
      && !this.subDomainName.toLowerCase().includes('localhost')) {
      objAuthConfig.clientId = this.subDomainName;
    }

    objAuthConfig.redirectUri = baseUrl + objAuthConfig.redirectUri;
    objAuthConfig.postLogoutRedirectUri = baseUrl + objAuthConfig.postLogoutRedirectUri;

    this.oAuthService.configure(objAuthConfig);
    this.oAuthService.setupAutomaticSilentRefresh();

    /// Used to store token in localStorage
    /// nounce were conflicted sometime during login in multiple tab, so sessionStorage is used insted of localStorage
    this.oAuthService.setStorage(sessionStorage);
  }

  async addSessionCurrentUser(sessionId, userId) {
    const model: UpdateSessionModel = {
      SessionId: sessionId,
      UserId: userId.sub || 0
    };
    let objAuthConfig = OAuthConfig;
    return this.http.post<any>(`${objAuthConfig.issuer.toString()}/Account/AddSessionCurrentUser`, model).toPromise();
  }

  async logOut() {
    return this.measureOperationTime(async () => {
    try {
        // Clear cache and storage in parallel
        await Promise.all([
            this.clearUserCache(),
            this.clearStorageItems()
        ]);

    if (this.isAuthenticated()) {
      if (!this.oAuthService.discoveryDocumentLoaded) {
        await this.loadDocument(true);
      } else {
        this.oAuthService.logOut(false);
        this.removeSessionRelatedItems();
      }
    } else {
      await this.router.navigate(["callback"]);
    }
  } catch (error) {
    console.error('Logout error:', error);
    // Implement fallback logout
    this.emergencyLogout();
}
}, 'Logout');
}

private emergencyLogout() {
// Clear all storage
localStorage.clear();
sessionStorage.clear();

// Remove all auth-related items
this.removeSessionRelatedItems();

// Reset the user object
this.user = null;

// Force redirect to login page
this.router.navigate(['callback'], { replaceUrl: true });
}

private async clearUserCache() {
  // Clear all user-related cache
  const cacheKeys = ['user_profile_cache', 'currentUser'];
  cacheKeys.forEach(key => localStorage.removeItem(key));
}

private async clearStorageItems() {
  // Optimize storage clearing
  const itemsToClear = [
      'currentUser',
      '_entityId',
      'service'
  ];
  itemsToClear.forEach(item => localStorage.removeItem(item));
}

public async measureOperationTime(operation: () => Promise<any>, operationName: string) {
const startTime = performance.now();
try {
    await operation();
} finally {
    const endTime = performance.now();
    console.debug(`${operationName} took ${endTime - startTime}ms`);
}
}

  async configureImplicitFlowAndPopulateUser() {
    if (this.isAuthenticated()) {
      if (!this.user) {
        await this.getUser();
      }
    } else {
      await this.authorizeAndLogin();
    }
  }

  public async authorizeAndLogin() {
    return this.measureOperationTime(async () => {
    try {
      await this.loadDiscoveryDocumentAndLogin();
      if (this.isAuthenticated()) {
        let sessionId = sessionStorage.session_state;
        const userId = sessionStorage.getItem('id_token_claims_obj');
        const res = await this.addSessionCurrentUser(sessionId, JSON.parse(userId || ""))
        if (res) {
          await this.getUser();
        }
        return true;
      }

      this.oAuthService.initImplicitFlow();
    } catch (error) {
      console.error('Authorization error:', error);
      await this.router.navigate(['/error/500']);
      }
    }, 'Authorization and Login');
  }

  public async checkAndLoadDiscoveryDocument() {
    if (!this.oAuthService.discoveryDocumentLoaded) {
      try {
        //await this.loadDiscoveryDocument();
        this.copySessionRelatedItems_SessionToLocaltorage();
        if (!this.user && localStorage.getItem('currentUser')) {
          this.user = JSON.parse(localStorage.getItem('currentUser'));
          this.validatedSubDomain();
        }
      } catch (error) {
        console.error('Check and Load Discovery Dcoument error:', error);
      }
    }
  }

  /// try to load document three times on error
  public async loadDocument(isLogoutAfterLoad: boolean = false, count: number = 0) {
    count++;
    if (count > 3)
      return;

    if (!this.oAuthService.discoveryDocumentLoaded) {
      try {
        await this.loadDiscoveryDocument();
        if (!this.user && localStorage.getItem('currentUser')) {
          this.user = JSON.parse(localStorage.getItem('currentUser'));
        }

        this.validatedSubDomain();

        if (isLogoutAfterLoad) {
          this.oAuthService.logOut(false);
        }
      } catch (error) {
        if (count == 3) {
          console.error("DiscoveryDocument not loaded", error)
          if (isLogoutAfterLoad) {
            await this.router.navigate(["/callback"]);
          } else {
            await this.router.navigate(["/error", "500"]);
          }
        } else {
          setTimeout(() => {
            this.loadDocument(isLogoutAfterLoad, count);
          }, 500);
        }
      }
    }
  }

  private async getUser() {
    //localStorage.setItem('_entity', 'API');
    localStorage.setItem('_entityId', '1');

    try {
      const userData = await this.userService.getUser();
      if (userData) {
        await this.mapProfileData(userData);
        // Cache the mapped profile
        this.cacheUserProfile(userData);
      } else {
        await this.logOut();
      }
    } catch (error) {
      console.error("Error while fetching userinfo", error);
      await this.router.navigate(["/error", "usernotfound"]);
    }
  }

  // Add caching mechanism
  private cacheUserProfile(userData: any) {
    const cacheKey = 'user_profile_cache';
    const cacheExpiry = Date.now() + (60 * 60 * 1000); // 30 minutes
    const cacheData = {
      data: userData,
      expiry: cacheExpiry
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  public async mapProfileData(response) {
    this.user = {
      id: response.id,
      fullName: response.fullName,
      userName: response.userName,
      orgImagePath: response.orgImagePath,
      profileImage: response.profileImageUrl,
      entityName: response.entityName,
      countryId: response.countryId,
      usertype: response.userType,
      customerid: response.customerId,
      supplierid: response.supplierId,
      inspectionCompanyid: response.inspectionCompanyId,
      factoryid: response.factoryId,
      staffId: response.staffId,
      profiles: response.userProfileList,
      showHeader: true,
      changePassword: true,
      userProfileList: response.userProfileList,
      appLanguage:response.appLanguage,
      roles: response.roles.map((x) => {
        let role: RoleModel = {
          id: x.id,
          roleName: x.roleName,
          qcRole: x.qcRole,
          roleType: x.roleType
        }
        return role;
      }),
      rights: response.rights,
      rightsDB: response.rights,
      permissions: response.permissions,
      packageFeatures: response.packageFeatures,
      packageId: response.packageId,
      defaultFeatureId: null, //response.defaultFeatureId,
      routes: ["/userDashBoard/dashboard"],
      subDomainName: response.subDomainName,
      customerContactId : response.customerContactId,

      supplierContactId: response.supplierContactId,
      isFirstTimeLogin: response.isFirstTimeLogin,
      customerName: response.customerName,
      supplierType: response.supplierType,
      supplierTypeId: response.supplierTypeId,
      isFooterIconIsShow : response.isFooterIconIsShow,
      isHeaderIconIsShow : response.isHeaderIconIsShow,
      isSupportIconIsShow: response.isSupportIconIsShow,
       pageIds: response.pageIds||[]
    }

    this.data = new UserLoginModel();
    this.data.user = this.user;
    localStorage.setItem('currentUser', JSON.stringify(this.data.user));
    localStorage.setItem("lang",  this.data.user.appLanguage || "en");
    this.utility.setLangSubject(localStorage.getItem('lang'))

    if (this.user.packageId != null)
      localStorage.setItem('service', JSON.stringify(this.user.defaultFeatureId));

    this.validatedSubDomain();
    await this.processSuccessLoginData();
  }

  private async processSuccessLoginData() {
    if (!this.user.changePassword) { // change password first time
      await this.router.navigate(["/changepassword"]);
    }
    if (this.user.permissions && this.user.permissions.length === 1 && this.user.permissions.includes(Permission.Change_Password_List)) { // only change password permission available
      await this.router.navigate(["/changepassword"]);
    }
    else if (this.user.routes.length > 0 && this.user.routes[0] != null && this.user.routes[0] != '') {
      let path = "/" + this.user.routes[0];
      await this.router.navigate([path])
    }
    else {
      await this.router.navigate(['/forbidden']);
    }
  }

  public isAuthenticated(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  private validatedSubDomain() {
    if (this.isAuthenticated() && this.user != null) {
      if (this.user.subDomainName
        && !this.subDomainName.toLowerCase().includes("localhost")
        && this.subDomainName.toLowerCase() != this.user.subDomainName.toLowerCase()) {
        this.oAuthService.logOut(false);
      }
    }
  }

  public getAccessToken(): string {
    return this.oAuthService?.getAccessToken();
  }

  public copySessionRelatedItems_LocalToSessionStorage() {
    this.copyStorageItem(localStorage, sessionStorage, 'expires_at');
    this.copyStorageItem(localStorage, sessionStorage, 'id_token_expires_at');
    this.copyStorageItem(localStorage, sessionStorage, 'access_token_stored_at');
    this.copyStorageItem(localStorage, sessionStorage, 'id_token_stored_at');
    this.copyStorageItem(localStorage, sessionStorage, 'id_token');
    this.copyStorageItem(localStorage, sessionStorage, 'access_token');
    this.copyStorageItem(localStorage, sessionStorage, 'id_token_claims_obj');
    this.copyStorageItem(localStorage, sessionStorage, 'session_state');
    this.copyStorageItem(localStorage, sessionStorage, 'granted_scopes');
  }

  private copySessionRelatedItems_SessionToLocaltorage() {
    this.copyStorageItem(sessionStorage, localStorage, 'expires_at');
    this.copyStorageItem(sessionStorage, localStorage, 'id_token_expires_at');
    this.copyStorageItem(sessionStorage, localStorage, 'access_token_stored_at');
    this.copyStorageItem(sessionStorage, localStorage, 'id_token_stored_at');
    this.copyStorageItem(sessionStorage, localStorage, 'id_token');
    this.copyStorageItem(sessionStorage, localStorage, 'access_token');
    this.copyStorageItem(sessionStorage, localStorage, 'id_token_claims_obj');
    this.copyStorageItem(sessionStorage, localStorage, 'session_state');
    this.copyStorageItem(sessionStorage, localStorage, 'granted_scopes');
  }

  private removeSessionRelatedItems() {
    let storages = [localStorage, sessionStorage];
    this.removeItemFormStorage(storages, 'expires_at');
    this.removeItemFormStorage(storages, 'id_token_expires_at');
    this.removeItemFormStorage(storages, 'access_token_stored_at');
    this.removeItemFormStorage(storages, 'id_token_stored_at');
    this.removeItemFormStorage(storages, 'id_token');
    this.removeItemFormStorage(storages, 'access_token');
    this.removeItemFormStorage(storages, 'id_token_claims_obj');
    this.removeItemFormStorage(storages, 'session_state');
    this.removeItemFormStorage(storages, 'granted_scopes');
  }

  private copyStorageItem(FromStorage: Storage, ToStorage: Storage, key: string) {
    let value = FromStorage.getItem(key);

    if (value)
      ToStorage.setItem(key, value);
  }

  private removeItemFormStorage(storages: Storage[], key: string) {
    if (storages) {
      storages.forEach(storage => {
        storage.removeItem(key);
      });
    }
  }

  private async loadDiscoveryDocument() {
    if (!this.oAuthService.discoveryDocumentLoaded) {
    await this.oAuthService.loadDiscoveryDocument();
    }
    this.copySessionRelatedItems_SessionToLocaltorage();
  }

  private async loadDiscoveryDocumentAndLogin() {
    if (!this.oAuthService.discoveryDocumentLoaded) {
      await this.oAuthService.loadDiscoveryDocumentAndLogin();
    }
    this.copySessionRelatedItems_SessionToLocaltorage();
  }
}
